import React, { useState } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Alert, CircularProgress } from "@mui/material";

const ResetPassword: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Get token and email from URL
    const token = searchParams.get("token");
    const email = searchParams.get("email");

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const onSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setMessage(null);
        setError(null);

        if (newPassword !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }

        if (!token || !email) {
            setError("Invalid password reset link.");
            return;
        }

        setLoading(true);

        try {
            const response = await fetch("/api/reset-password-public", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    email: email,
                    token: token,
                    newPassword: newPassword
                }),
            });

            if (response.ok) {
                setSuccess(true);
                setMessage("Password has been reset successfully. Redirecting to login...");
                setTimeout(() => {
                    navigate("/login");
                }, 3000);
            } else {
                const data = await response.json().catch(() => null);
                let errorMsg = "Failed to reset password. The link may have expired.";
                if (data && Array.isArray(data)) {
                    errorMsg = data.map((e: any) => e.description || e.code).join(", ");
                } else if (data && typeof data === 'string') {
                    errorMsg = data;
                }
                setError(errorMsg);
            }
        } catch (err) {
            setError("Failed to connect to the server.");
        } finally {
            setLoading(false);
        }
    };

    if (!token || !email) {
        return (
            <Container component="main" maxWidth="sm">
                <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Alert severity="error">Invalid or missing password reset token.</Alert>
                    <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate("/login")}>
                        Go to Login
                    </Button>
                </Box>
            </Container>
        );
    }

    return (
        <Container component="main" maxWidth="sm">
            <Box
                sx={{
                    boxShadow: 3,
                    borderRadius: 2,
                    px: 4,
                    py: 6,
                    marginTop: 8,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    backgroundColor: "white",
                }}
            >
                <Typography component="h1" variant="h5">
                    Reset Password
                </Typography>

                {message && (
                    <Alert severity="success" sx={{ mt: 2, width: '100%' }}>
                        {message}
                    </Alert>
                )}

                {error && (
                    <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
                        {error}
                    </Alert>
                )}

                <Box component="form" onSubmit={onSubmit} noValidate sx={{ mt: 1, width: "100%" }}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="newPassword"
                        label="New Password"
                        type="password"
                        id="newPassword"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        disabled={loading || success}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="confirmPassword"
                        label="Confirm New Password"
                        type="password"
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={loading || success}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2 }}
                        disabled={loading || success}
                    >
                        {loading ? <CircularProgress size={24} /> : "Reset Password"}
                    </Button>
                </Box>
            </Box>
        </Container>
    );
};

export default ResetPassword;
